import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { shareService } from '../../services/shareService';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { ShareModal } from '../../components/app/ShareModal';
import { isPast, isToday, startOfDay } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

export const Dashboard = () => {
  const { profile, user } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [relationshipSummary, setRelationshipSummary] = useState(null);
  const [sharingStats, setSharingStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [defaultCard, setDefaultCard] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);

  const firstName = profile?.full_name?.split(' ')[0] || 'User';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cardsRes, leadsRes, remindersRes, statsRes] = await Promise.all([
          supabase.from('cards').select('*').eq('user_id', user.id).neq('status', 'archived').order('created_at', { ascending: false }),
          supabase.from('card_leads').select('id, status, is_important, created_at').eq('user_id', user.id).eq('is_archived', false),
          supabase.from('connection_reminders').select('due_date').eq('user_id', user.id).eq('is_completed', false),
          shareService.getDashboardStats(user.id)
        ]);

        if (cardsRes.error) throw cardsRes.error;
        const allCards = cardsRes.data || [];
        setCards(allCards);
        setSharingStats(statsRes);

        if (profile?.default_card_id) {
          const dCard = allCards.find(c => c.id === profile.default_card_id);
          if (dCard && dCard.status === 'published') setDefaultCard(dCard);
        }

        const leads = leadsRes.data || [];
        const reminders = remindersRes.data || [];
        const now = new Date();
        const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30));

        let dueToday = 0;
        let overdue = 0;

        reminders.forEach(r => {
          const due = startOfDay(new Date(r.due_date));
          if (isPast(due) && !isToday(due)) overdue++;
          if (isToday(due)) dueToday++;
        });

        setRelationshipSummary({
          total: leads.length,
          newLast30: leads.filter(l => new Date(l.created_at) >= thirtyDaysAgo).length,
          important: leads.filter(l => l.is_important).length,
          active: leads.filter(l => ['following_up', 'active', 'client'].includes(l.status)).length,
          dueToday,
          overdue
        });
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) fetchData();
  }, [user, profile?.default_card_id]);

  if (loading) return <div className="flex justify-center py-32"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-10 max-w-6xl pb-16">
      {showShareModal && defaultCard && (
        <ShareModal card={defaultCard} user={user} onClose={() => setShowShareModal(false)} />
      )}

      {/* Header - Refined typography */}
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">
            Welcome back, {firstName}
          </h1>
          <p className="mt-2 text-[15px] font-medium text-slate-500">
            You have {cards.length} active digital cards.
          </p>
        </div>
        <div className="flex gap-3">
          {defaultCard && (
            <Button variant="primary" onClick={() => setShowShareModal(true)} className="bg-slate-900">
              <SafeIcon icon={FiIcons.FiShare2} className="mr-2 h-4 w-4" /> Quick Share
            </Button>
          )}
          <Button variant="secondary" onClick={() => navigate('/app/cards/new')}>
            <SafeIcon icon={FiIcons.FiPlus} className="mr-2 h-4 w-4" /> Create Card
          </Button>
        </div>
      </header>

      {/* Review My Networking Banner - Premium SaaS styling */}
      <div className="bg-slate-950 rounded-[2rem] p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-8 shadow-xl border border-slate-800 relative overflow-hidden group">
        <div className="absolute right-0 top-0 w-96 h-96 bg-blue-500 rounded-full blur-[100px] opacity-20 -mr-20 -mt-20 pointer-events-none transition-opacity duration-700 group-hover:opacity-30"></div>
        <div className="relative z-10 flex items-start gap-6">
          <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center shrink-0 backdrop-blur-md border border-white/10 shadow-inner">
            <SafeIcon icon={FiIcons.FiTarget} className="text-3xl text-blue-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-2 text-white tracking-tight">Review My Networking</h2>
            <p className="text-slate-400 text-sm max-w-lg leading-relaxed font-medium">
              Get an evidence-backed briefing on what generated the best results, which relationships need attention, and what to do next.
            </p>
          </div>
        </div>
        <Button 
          onClick={() => navigate('/app/review')} 
          className="relative z-10 w-full md:w-auto bg-white text-slate-900 hover:bg-slate-100 border-0 shadow-sm whitespace-nowrap rounded-xl px-6 py-3 font-bold"
        >
          Generate Briefing
        </Button>
      </div>

      {/* Relationship Summary - Softer borders, refined spacing */}
      <div>
        <div className="flex justify-between items-end mb-5">
          <h2 className="text-lg font-bold text-slate-900 tracking-tight">Relationship Intelligence</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate('/app/connections')} className="text-slate-500 hover:text-slate-900 font-medium">
            Manage Connections <SafeIcon icon={FiIcons.FiArrowRight} className="ml-2" />
          </Button>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white p-6 rounded-2xl border border-slate-200/60 shadow-sm flex flex-col cursor-pointer hover:border-slate-300 hover:shadow-md transition-all duration-200" onClick={() => navigate('/app/connections')}>
            <div className="text-blue-500 mb-3"><SafeIcon icon={FiIcons.FiUsers} className="text-2xl" /></div>
            <p className="text-3xl font-bold text-slate-900 tracking-tight">{relationshipSummary?.newLast30}</p>
            <p className="text-sm font-semibold text-slate-500 mt-1">New (30d)</p>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-slate-200/60 shadow-sm flex flex-col cursor-pointer hover:border-slate-300 hover:shadow-md transition-all duration-200" onClick={() => navigate('/app/connections?filter=important')}>
            <div className="text-amber-500 mb-3"><SafeIcon icon={FiIcons.FiStar} className="text-2xl fill-current" /></div>
            <p className="text-3xl font-bold text-slate-900 tracking-tight">{relationshipSummary?.important}</p>
            <p className="text-sm font-semibold text-slate-500 mt-1">Important</p>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-slate-200/60 shadow-sm flex flex-col cursor-pointer hover:border-slate-300 hover:shadow-md transition-all duration-200" onClick={() => navigate('/app/connections')}>
            <div className="text-indigo-500 mb-3"><SafeIcon icon={FiIcons.FiActivity} className="text-2xl" /></div>
            <p className="text-3xl font-bold text-slate-900 tracking-tight">{relationshipSummary?.active}</p>
            <p className="text-sm font-semibold text-slate-500 mt-1">Active Deals</p>
          </div>
          <div className="bg-amber-50/50 p-6 rounded-2xl border border-amber-100 shadow-sm flex flex-col cursor-pointer hover:border-amber-200 hover:shadow-md transition-all duration-200" onClick={() => navigate('/app/reminders')}>
            <div className="text-amber-600 mb-3"><SafeIcon icon={FiIcons.FiCalendar} className="text-2xl" /></div>
            <p className="text-3xl font-bold text-amber-900 tracking-tight">{relationshipSummary?.dueToday}</p>
            <p className="text-sm font-semibold text-amber-700 mt-1">Due Today</p>
          </div>
          <div className="bg-red-50/50 p-6 rounded-2xl border border-red-100 shadow-sm flex flex-col cursor-pointer hover:border-red-200 hover:shadow-md transition-all duration-200" onClick={() => navigate('/app/reminders')}>
            <div className="text-red-500 mb-3"><SafeIcon icon={FiIcons.FiAlertCircle} className="text-2xl" /></div>
            <p className="text-3xl font-bold text-red-900 tracking-tight">{relationshipSummary?.overdue}</p>
            <p className="text-sm font-semibold text-red-700 mt-1">Overdue</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Cards List Preview */}
        <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm overflow-hidden flex flex-col">
          <div className="px-6 py-5 border-b border-slate-100/60 flex items-center justify-between bg-slate-50/50">
            <h2 className="text-base font-bold text-slate-900 tracking-tight">Your Cards</h2>
            <Link to="/app/cards" className="text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors">
              View all
            </Link>
          </div>
          <div className="divide-y divide-slate-100/60">
            {cards.slice(0, 3).map((card) => (
              <div key={card.id} className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50/80 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 border border-slate-200/60 shrink-0">
                    {card.profile_image_url ? (
                      <img src={card.profile_image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <SafeIcon icon={FiIcons.FiUser} className="text-xl" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 flex items-center tracking-tight">
                      {card.internal_name}
                      {profile?.default_card_id === card.id && <SafeIcon icon={FiIcons.FiStar} className="ml-1.5 text-amber-400 fill-current" title="Default sharing card" />}
                    </h3>
                    <div className="text-xs font-medium text-slate-500 mt-1">
                      <span className={`capitalize ${card.status === 'published' ? 'text-emerald-600' : ''}`}>
                        {card.status}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/app/cards/${card.id}/edit`)}>Edit</Button>
                  {card.status === 'published' ? (
                    <Button variant="secondary" size="sm" onClick={() => window.open(`#/c/${card.public_slug}`, '_blank')}>Public</Button>
                  ) : (
                    <Button variant="secondary" size="sm" onClick={() => navigate(`/app/cards/${card.id}`)}>Details</Button>
                  )}
                </div>
              </div>
            ))}
            {cards.length === 0 && (
              <div className="p-10 text-center text-slate-500 text-sm font-medium">You haven't created any cards yet.</div>
            )}
          </div>
        </div>

        {/* Sharing & Distribution Stats */}
        <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm overflow-hidden flex flex-col">
          <div className="px-6 py-5 border-b border-slate-100/60 flex items-center justify-between bg-slate-50/50">
            <h2 className="text-base font-bold text-slate-900 tracking-tight">Sharing & Distribution</h2>
            <Link to="/app/cards" className="text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors">
              Share Centre
            </Link>
          </div>
          <div className="p-6 flex-1 flex flex-col justify-center">
            {sharingStats && sharingStats.total > 0 ? (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center mr-4 text-blue-500 border border-blue-100/50">
                      <SafeIcon icon={FiIcons.FiLink} className="text-lg" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900 tracking-tight">Links Copied</p>
                      <p className="text-xs font-medium text-slate-500 mt-0.5">Direct URLs shared</p>
                    </div>
                  </div>
                  <span className="text-xl font-bold text-slate-900 tracking-tight">{sharingStats.links}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center mr-4 text-purple-500 border border-purple-100/50">
                      <SafeIcon icon={FiIcons.FiMaximize} className="text-lg" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900 tracking-tight">QR Codes</p>
                      <p className="text-xs font-medium text-slate-500 mt-0.5">Vector & Images downloaded</p>
                    </div>
                  </div>
                  <span className="text-xl font-bold text-slate-900 tracking-tight">{sharingStats.qrs}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center mr-4 text-emerald-500 border border-emerald-100/50">
                      <SafeIcon icon={FiIcons.FiShare2} className="text-lg" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900 tracking-tight">Social Shares</p>
                      <p className="text-xs font-medium text-slate-500 mt-0.5">LinkedIn, WhatsApp, etc.</p>
                    </div>
                  </div>
                  <span className="text-xl font-bold text-slate-900 tracking-tight">{sharingStats.social}</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-10 text-slate-500">
                <SafeIcon icon={FiIcons.FiShare2} className="text-4xl mx-auto mb-4 text-slate-300" />
                <p className="text-sm font-medium text-slate-900 mb-1">No shares recently</p>
                <p className="text-xs">Open the Share Centre on any published card to generate assets.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};